import * as Icons from "@mui/icons-material";
import { Box, IconButton, Toolbar, useTheme } from "@mui/material";

import { createLogger } from "@/logger";

const log = createLogger("bootstrap");

import React, { useContext, useEffect, useRef } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";

import CurrentEnvironment from "@/components/electron/CurrentEnvironment.tsx";
import ElectronComponentLoader from "@/components/electron/ElectronComponentLoader.tsx";
import PersonMenu from "@/components/Icons/PersonMenu";
import InvalidWorkspaceModal from "@/components/InvalidWorkspaceModal";
import LoadingContainer from "@/components/LoadingContainer";
import LoadingScreen from "@/components/LoadingScreen";
import MainContent from "@/components/MainContent";
import MembersDrawer from "@/components/MembersDrawer";
import SBAppBar from "@/components/SBAppBar";
import ScheduleMessageModal from "@/components/Scheduling/ScheduleMessageModal";
import SearchContainer from "@/components/Search/SearchContainer";
import UserFeedbackModal from "@/components/UserFeedbackModal";
import InviteToWorkspaceModal from "@/components/Workspace/InviteToWorkspaceModal";
import WorkspaceDrawer from "@/components/Workspace/WorkspaceDrawer";
import useLocationChange from "@/hooks/useLocationChange";
import UseWorkspaceMembershipAbilities from "@/hooks/useWorkspaceMembershipAbilities";
import Locator from "@/locator";
import { AudioAppContext } from "@/models/AudioAppContextProvider";
import { DataContext } from "@/models/DataProvider";
import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery.ts";
import { fetchFeedCommandAliasesQuery } from "@/models/repositories";
import { CurrentFeedContext } from "@/models/StateProviders/currentFeedProvider";
import { UserContext } from "@/models/StateProviders/userProvider.tsx";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { TrackingContext } from "@/models/TrackingStateProvider";
import { UnreadsContext } from "@/models/UnreadsContextProvider";
import { UxContext } from "@/models/UxStateProvider";
import CombinedHeaderContent from "@/routes/workspace/CombinedHeaderContent.tsx";
import { MAIN_DRAWER_WIDTH, MEMBERS_DRAWER_WIDTH } from "@/utils";

export default function Workspace() {
  const { workspaceId } = useContext(UserContext);
  const theme = useTheme();
  const { ampli } = useContext(TrackingContext);
  const { unreadFeedIds } = React.useContext(UnreadsContext);
  const navigate = useNavigate();
  const { bootstrapComplete, firstBootWorkspaceId, invalidWorkspace } = React.useContext(DataContext);
  const { currentFeedId } = useContext(CurrentFeedContext);
  const params = useParams();
  const urlWorkspaceId = params.workspaceId;
  const { workspaceMemberships, workspaces } = React.useContext(WorkspaceContext);
  const { setToolbarSize, globalSearchOpen } = React.useContext(UxContext);
  const showLoadingWorkspaceMembershipsContainer = workspaceMemberships?.length === 0;
  const feedId = currentFeedId;

  const aliasChannelDetails =
    useReactiveSelectQuery(fetchFeedCommandAliasesQuery, [feedId], "Workspace:index:fetchFeedCommandAliasesQuery") ??
    [];

  const aliasChannel = aliasChannelDetails?.length === 1 ? aliasChannelDetails[0] : null;

  type NonFeedPage = {
    label: string;
    key: string;
    active: boolean;
  };
  const nonFeedPages: NonFeedPage[] = [
    { label: "User Management", key: "user-management", active: false },
    { label: "Scheduled Messages", key: "scheduled-messages", active: false },
    { label: "Template Management", key: "template-management", active: false },
  ].map((page) => ({ ...page, active: window?.location?.pathname.includes(page.key) }));
  const activeNonFeedPage = nonFeedPages?.find((i: NonFeedPage) => i.active);
  const isNonFeedPage =
    activeNonFeedPage && nonFeedPages?.some((page) => window?.location?.pathname.includes(page.key));
  const headerType = isNonFeedPage ? "non-feed" : aliasChannel ? "alias-channel" : "feed";

  useEffect(() => {
    if (globalSearchOpen) {
      ampli.searchClick({ workspaceId });
    }
  }, [globalSearchOpen, workspaceId, ampli.searchClick]);

  const { playerPlaying, pauseItem } = React.useContext(AudioAppContext);

  const { isSmUp, leftNavOpen, toggleLeftNav, rightNavOpen, toggleRightNav } = React.useContext(UxContext);
  const [url, setUrl] = React.useState<string | null>(null);

  useLocationChange((location) => {
    const baseUrl = window.location.origin;
    // Don't Stop Queue -> If dsq is present in the URL, don't reset the queue
    const dsq = new URLSearchParams(window?.location?.search).get("dsq") ?? null;
    setUrl(baseUrl + location.pathname);
    if (playerPlaying && !dsq) {
      pauseItem();
    }
  });

  const drawerStyles = {
    width: isSmUp ? MAIN_DRAWER_WIDTH : "100%",
    flexShrink: 0,
    pointerEvents: leftNavOpen ? "auto" : "none",
    "& .MuiDrawer-paper": {
      width: isSmUp ? MAIN_DRAWER_WIDTH : "100%",
      boxSizing: "border-box",
    },
  };

  const memberDrawerStyles = {
    zIndex: 0,
    position: "relative",
    left: "430px",
    width: isSmUp ? MEMBERS_DRAWER_WIDTH : "100%",
    flexShrink: 0,
    pointerEvents: rightNavOpen ? "auto" : "none",
    "& .MuiDrawer-paper": {
      width: isSmUp ? MEMBERS_DRAWER_WIDTH : "100%",
      boxSizing: "border-box",
    },
  };

  React.useEffect(() => {
    log.info("Checking Workspaces", {
      bootstrapComplete,
      firstBootWorkspaceId,
      urlWorkspaceId,
      workspaceId,
      workspaces,
    });
    if (!bootstrapComplete) {
      return;
    }

    if (urlWorkspaceId) {
      return;
    }

    if (firstBootWorkspaceId && !urlWorkspaceId && !workspaceId) {
      try {
        navigate(`/workspaces/${firstBootWorkspaceId}`);
        return;
      } catch (e) {
        log.error(e);
      }
    } else if (!firstBootWorkspaceId && !urlWorkspaceId && workspaces?.length > 0) {
      const topId = workspaces?.[0].id;
      try {
        navigate(`/workspaces/${topId}`);
        return;
      } catch (e) {
        console.error({ e });
      }
    } else if (workspaceId) {
      navigate(`/workspaces/${workspaceId}`);
      return;
    }
  }, [navigate, workspaceId, urlWorkspaceId, firstBootWorkspaceId, workspaces, bootstrapComplete]);

  const toolbarContainer = useRef(null);

  const resizeWindow = () => {
    setToolbarSize({
      offsetWidth: toolbarContainer?.current?.offsetWidth,
      offsetLeft: toolbarContainer?.current?.offsetLeft,
    });
  };

  useEffect(() => {
    window.addEventListener("resize", resizeWindow);
    return () => {
      window.removeEventListener("resize", resizeWindow);
    };
  });

  UseWorkspaceMembershipAbilities();

  useEffect(() => {
    if (toolbarContainer?.current) {
      resizeWindow();
    }
  }, [setToolbarSize, toolbarContainer.current, globalSearchOpen, leftNavOpen]);

  if (invalidWorkspace) {
    return <InvalidWorkspaceModal open={true} allowClose={false} workspaceId={workspaceId} />;
  }

  if (!bootstrapComplete) {
    return <LoadingScreen />;
  }

  return (
    <>
      <ElectronComponentLoader component={<CurrentEnvironment />} />
      <InviteToWorkspaceModal />
      <ScheduleMessageModal />
      <UserFeedbackModal />
      <Box sx={{ display: "flex" }} data-feed-layout-wrap>
        <SBAppBar data-app-bar position="fixed">
          <Toolbar
            ref={toolbarContainer}
            data-toolbar
            sx={{
              display: "flex",
              width: "100%",
              gap: 1,
              margin: "0 auto",
              px: { xs: 1, sm: 2 },
            }}
          >
            <Box sx={{ display: "flex" }}>
              <IconButton
                aria-label={Locator.mainNav.workspaceNavToggle}
                onClick={() => {
                  toggleLeftNav();
                  resizeWindow();
                }}
                edge="start"
                sx={{
                  background: "transparent",
                  borderRadius: "1000px",
                  ml: 0,
                }}
              >
                {leftNavOpen ? <Icons.ArrowBack role="img" /> : <Icons.Menu role="img" />}
                {!leftNavOpen && unreadFeedIds?.length > 0 ? (
                  <Box
                    aria-label={Locator.workspaceNav.channels.list.unreadCount}
                    sx={{
                      position: "absolute",
                      bottom: 10,
                      right: 8,
                      borderRadius: "50%",
                      background: theme.palette.brand.secondary.main,
                      height: 10,
                      width: 10,
                      minWidth: 10,
                    }}
                  />
                ) : null}
              </IconButton>
            </Box>
            <CombinedHeaderContent
              type={headerType}
              aliasChannel={aliasChannel}
              activeNonFeedPageLabel={activeNonFeedPage?.label}
            />
            <Box sx={{ display: "flex" }}>
              <IconButton
                sx={{
                  background: "transparent",
                  borderRadius: "1000px",
                  width: 44,
                }}
                aria-label={Locator.mainNav.membersListToggle}
                onClick={toggleRightNav}
                edge="start"
              >
                {rightNavOpen ? <Icons.ArrowForward role="img" /> : <PersonMenu role="img" />}
              </IconButton>
            </Box>
          </Toolbar>
        </SBAppBar>
        <WorkspaceDrawer url={url} sx={drawerStyles} />
        <MainContent
          leftOpen={leftNavOpen}
          rightOpen={rightNavOpen}
          sx={{
            height: "100svh",
            pt: "92px",
          }}
        >
          <SearchContainer />
          {showLoadingWorkspaceMembershipsContainer ? (
            <LoadingContainer showLoading={true}>Loading Workspace</LoadingContainer>
          ) : (
            <Outlet />
          )}
        </MainContent>
        <MembersDrawer handleClose={toggleRightNav} url={url} sx={memberDrawerStyles} />
      </Box>
    </>
  );
}
